<template>
	<div>
		<div class="row content-header">
			<div class="content-header-left mb-2 col-md-9 col-12">
				<div class="row breadcrumbs-top">
					<div class="col-12">
						<h2 class="content-header-title float-left pr-1 mb-0">Permission</h2>
						<div class="breadcrumb-wrapper">
							<ol class="breadcrumb">
								<li class="breadcrumb-item active">
									<span aria-current="location"> List</span>
								</li>
							</ol>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Table Container Card -->
		<b-card no-body>
			<div class="px-5" v-if="roles && roles.length > 0">
				<!--
					One role is selected here, ONCE, for the whole page — every module
					panel below reads/writes permissions for this same role. Previously
					each module had its own vertical tab-list to pick a role, but every
					one of those tab-lists was wired to the SAME underlying variable
					(selectedRoleTabIndex), so they fought each other: clicking a role
					in one module's tabs could leave a DIFFERENT module (or the header
					above) still displaying a stale role. The actual grant/revoke calls
					were unaffected (they always used the live value correctly), but the
					on-screen state was unreliable enough that an owner genuinely
					couldn't trust which role they were about to change — reported as
					"confusing... need to know which role I'm giving permission to".
					A single always-visible selector removes the ambiguity entirely.
				-->
				<div class="permission-role-picker mb-2 pb-2">
					<div class="mb-1 text-muted font-weight-bold">Select Role to Edit</div>
					<div class="d-flex flex-wrap" style="gap: 0.5rem">
						<b-button
							v-for="role in roles"
							:key="role.id"
							size="sm"
							:variant="role.id === selectedRoleId ? 'primary' : 'outline-secondary'"
							@click="selectedRoleId = role.id"
						>
							{{ role.name }}
						</b-button>
					</div>
				</div>
				<div
					v-if="selectedRoleId"
					class="d-flex align-items-center justify-content-between flex-wrap permission-bulk-actions mb-2 pb-2"
				>
					<h5 class="mb-0">
						Editing permissions for: <span class="text-primary">{{ selectedRoleName }}</span>
					</h5>
					<div>
						<b-button
							variant="success"
							size="sm"
							class="mr-1"
							:disabled="bulkActionLoading"
							@click="grantAllPermissions"
						>
							Select All Permissions for {{ selectedRoleName }}
						</b-button>
						<b-button
							variant="outline-danger"
							size="sm"
							:disabled="bulkActionLoading"
							@click="clearAllPermissions"
						>
							Clear All Permissions for {{ selectedRoleName }}
						</b-button>
					</div>
				</div>
				<app-collapse
					accordion
					:type="collapseType"
					v-for="modules in allModules"
					:key="modules.id"
					v-model="modules_id"
				>
					<app-collapse-item :title="modules.module_name" :value="modules.id" class="collapse-card">
						<!-- Table Container Card -->
						<b-card no-body>
							<div class="table-header">
								<h4 class="header-title">
									Permission <span class="text-primary">— {{ selectedRoleName }}</span>
								</h4>

								<div class="permission-view row">
									<div
										v-for="permission in modules.permission"
										:key="permission.id"
										class="custom-control custom-checkbox d-flex col-md-3 mb-1"
									>
										<input
											type="checkbox"
											name="permission"
											style="margin-top: 5px"
											:value="permission.id"
											:checked="checkPermission(permission.id, selectedRoleId)"
											@change="givePermission(permission.id, selectedRoleId)"
										/>
										<div class="ml-1">{{ permission.name }}</div>
									</div>
								</div>
							</div>
						</b-card>
					</app-collapse-item>
				</app-collapse>
			</div>
			<div v-else class="d-flex flex-column align-items-center py-5">
				<b-img :src="require('@/assets/images/svg/infographics/no_data_infographic.svg')" class="mr-2" />
				<h4 class="mt-2 Secondary">No modules found</h4>
			</div>
		</b-card>
	</div>
</template>

<script>
import vSelect from "vue-select";
import AppCollapse from "@core/components/app-collapse/AppCollapse.vue";
import AppCollapseItem from "@core/components/app-collapse/AppCollapseItem.vue";

import {
	BCard,
	BRow,
	BImg,
	BCol,
	BFormInput,
	BButton,
	BTable,
	BMedia,
	BAvatar,
	BLink,
	BBadge,
	BDropdown,
	BDropdownItem,
	BPagination,
	BTooltip,
	BTabs,
	BTab,
	BCardText,
	BFormDatepicker,
	BFormSelect,
	BFormCheckbox,
	BFormGroup,
} from "bootstrap-vue";

import PermissionList from "./PermissionList.vue";
import { methods } from "vue-echarts";
import axiosIns from "@/libs/axios";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
	components: {
		BCard,
		BFormCheckbox,
		BRow,
		BCol,
		BImg,
		BFormInput,
		BButton,
		BTable,
		BMedia,
		BAvatar,
		BLink,
		BBadge,
		BDropdown,
		BDropdownItem,
		BPagination,
		BTooltip,
		BCardText,
		BTabs,
		BTab,

		BFormDatepicker,
		BFormSelect,
		AppCollapse,
		AppCollapseItem,
		PermissionList,
		BFormGroup,
	},
	data() {
		return {
			collapseType: "default",
			allModules: {},
			roles: [],
			// company: {},
			// selected: null,
			// checked: false,
			// role_id: null,
			// permission_id: null,
			allPermissions: null,
			modules_id: null,
			selectedRoleId: null,
			bulkActionLoading: false,
		};
	},
	created: function () {
		this.getAssignedPermission();
		this.getCompanyModules();
	},
	methods: {
		getCompanyModules() {
			axiosIns
				.get(`web/modules`)
				.then((response) => {
					this.allModules = response.data.module.modules;
					this.roles = response.data.module.roles;
					// this.allPermissions = response.data.allPermissions;
					if (!this.selectedRoleId && this.roles.length > 0) {
						this.selectedRoleId = this.roles[0].id;
					}
				})
				.catch((error) => {
					const data = error.response?.data?.message;
					data, "data";
					this.$toast({
						component: ToastificationContent,
						position: "top-right",
						props: {
							icon: "CoffeeIcon",
							variant: "danger",
							text: data,
						},
					});
				});
		},

		givePermission(permission_id, role_id) {
			var data = new FormData();
			data.append("permission_id", permission_id);
			data.append("role_id", role_id);
			axiosIns
				.post(`web/permissions`, data)
				.then((res) => {
					this.$toast({
						component: ToastificationContent,
						position: "top-right",
						props: {
							icon: "CoffeeIcon",
							variant: "success",
							text: `Data Successfully Added `,
						},
					});
					this.checkPermission(permission_id, role_id);
				})
				.catch((error) => {
					const data = error.response?.data?.errors;
					data, "data";
					this.$toast({
						component: ToastificationContent,
						position: "top-right",
						props: {
							icon: "CoffeeIcon",
							variant: "danger",
							text: data,
						},
					});
				});
		},

		getAssignedPermission() {
			let url = `web/module/permissions`;
			axiosIns
				.get(this.addQueryString(url))
				.then((response) => {
					this.assigned_permissions = response.data.rolePermissions;
				})
				.catch((error) => {
					this.$toast({
						component: ToastificationContent,
						position: "top-right",
						props: {
							icon: "CoffeeIcon",
							variant: "danger",
							text:
								error.response?.data?.message ||
								error.response?.data?.error ||
								(error.response?.data?.errors
									? Object.values(error.response.data.errors).flat().join(", ")
									: "Something went wrong. Please try again."),
						},
					});
				});
		},

		checkPermission(permission_id, role_id) {
			if (role_id != null && permission_id != null) {
				let assignedPermission = this.assigned_permissions;
				let permission = assignedPermission.find((element) => {
					return element.permission_id == permission_id && element.role_id == role_id;
				});

				if (permission) {
					return true;
				} else {
					return false;
				}
				// }
			}
		},

		// Grants every permission across every module for the currently
		// selected role in one request, instead of the ~100+ individual
		// givePermission() clicks this screen used to require.
		grantAllPermissions() {
			if (!this.selectedRoleId || this.bulkActionLoading) return;
			this.bulkActionLoading = true;
			var data = new FormData();
			data.append("role_id", this.selectedRoleId);
			axiosIns
				.post(`web/permissions/grant-all`, data)
				.then((res) => {
					this.$toast({
						component: ToastificationContent,
						position: "top-right",
						props: {
							icon: "CoffeeIcon",
							variant: "success",
							text: res.data?.message || "All permissions granted successfully !",
						},
					});
					this.getAssignedPermission();
				})
				.catch((error) => {
					const data = error.response?.data?.message;
					this.$toast({
						component: ToastificationContent,
						position: "top-right",
						props: {
							icon: "CoffeeIcon",
							variant: "danger",
							text: data || "Failed to grant all permissions.",
						},
					});
				})
				.finally(() => {
					this.bulkActionLoading = false;
				});
		},

		// Symmetric counterpart — revokes every permission for the currently
		// selected role. Destructive, so confirm first (same $bvModal.msgBoxConfirm
		// pattern used elsewhere in this app, e.g. VetApprovals.vue).
		async clearAllPermissions() {
			if (!this.selectedRoleId || this.bulkActionLoading) return;
			let ok = false;
			try {
				ok = await this.$bvModal.msgBoxConfirm(
					`Clear ALL permissions for "${this.selectedRoleName}"? This will remove every permission currently granted to this role.`,
					{
						title: "Clear All Permissions",
						okVariant: "danger",
						okTitle: "Clear All",
						cancelTitle: "Cancel",
					}
				);
			} catch (e) {
				ok = false;
			}
			if (!ok) return;

			this.bulkActionLoading = true;
			var data = new FormData();
			data.append("role_id", this.selectedRoleId);
			axiosIns
				.post(`web/permissions/revoke-all`, data)
				.then((res) => {
					this.$toast({
						component: ToastificationContent,
						position: "top-right",
						props: {
							icon: "CoffeeIcon",
							variant: "success",
							text: res.data?.message || "All permissions cleared successfully !",
						},
					});
					this.getAssignedPermission();
				})
				.catch((error) => {
					const data = error.response?.data?.message;
					this.$toast({
						component: ToastificationContent,
						position: "top-right",
						props: {
							icon: "CoffeeIcon",
							variant: "danger",
							text: data || "Failed to clear all permissions.",
						},
					});
				})
				.finally(() => {
					this.bulkActionLoading = false;
				});
		},
	},
	computed: {
		selectedRoleName: function () {
			const role = this.roles.find((r) => r.id === this.selectedRoleId);
			return role ? role.name : "";
		},
	},
};
</script>

<style lang="scss" scoped>
.per-page-selector {
	width: 90px;
}
.header-title {
	height: 60px;
	border-bottom: 1px solid #ebe9f1;
	font-weight: 500;
	color: #636363;
	font-size: 1.714rem;
	margin-bottom: 1rem;
	padding: 1rem 1rem;
}

.invoice-filter-select {
	min-width: 190px;

	::v-deep .vs__selected-options {
		flex-wrap: nowrap;
	}

	::v-deep .vs__selected {
		width: 100px;
	}
}
.permission-view {
	height: 300px;
	overflow: auto;
}
.permission-bulk-actions {
	border-bottom: 1px solid #ebe9f1;
}
.permission-role-picker {
	border-bottom: 1px solid #ebe9f1;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/pages/nav-role.scss";
</style>
